import React, { useEffect, useState } from "react";
import { Button, Form, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaSearch,
  FaChevronDown,
  FaEllipsisV,
} from "react-icons/fa";
import TablePaginationBar from "../TablePaginationBar";
import ClinicActionsDropdown, {
  MENU_WIDTH,
  MENU_HEIGHT,
} from "./ClinicActionsDropdown";
import AddEditClinicModal from "./AddEditClinicModal";
import DeactivateClinicModal from "./DeactivateClinicModal";

const clinicStatusColors = {
  Active: { bg: "#D0FAE5", color: "#006145" },
  Inactive: { bg: "#FEE2E2", color: "#991B1B" },
};

const StatusPill = ({ status }) => {
  const colors = clinicStatusColors[status] || clinicStatusColors.Active;
  return (
    <span
      style={{
        backgroundColor: colors.bg,
        color: colors.color,
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 600,
        display: "inline-block",
      }}
    >
      {status}
    </span>
  );
};

const ClinicsTab = () => {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [actionClinic, setActionClinic] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const fetchClinics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user_id");
      const params = new URLSearchParams();
      if (statusFilter !== "All") params.append("status", statusFilter);
      if (search.trim()) params.append("search", search.trim());

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-clinics?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success) {
        setClinics(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching clinics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchClinics();
    }, search ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [statusFilter, search]);

  const totalPages = Math.ceil(clinics.length / rowsPerPage) || 1;
  const paginatedData = clinics.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, rowsPerPage]);

  const toggleDropdown = (id, e) => {
    e.stopPropagation();
    if (openDropdownId === id) {
      setOpenDropdownId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = MENU_WIDTH;
      const menuHeight = MENU_HEIGHT;
      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8;
      }
      if (top + menuHeight > window.innerHeight - 8) {
        top = rect.top - menuHeight - 4;
      }
      if (top < 8) top = 8;
      setDropdownPos({ top, left });
      setOpenDropdownId(id);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".clinic-actions-menu") &&
        !event.target.closest(".actions-menu-btn")
      ) {
        setOpenDropdownId(null);
      }
    };
    if (openDropdownId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  const handleClinicAction = (action, clinic) => {
    setOpenDropdownId(null);
    switch (action) {
      case "view":
        navigate(`/clinic-detail/${clinic.id}`);
        break;
      case "edit":
        setActionClinic(clinic);
        setShowEditModal(true);
        break;
      case "deactivate":
        setActionClinic(clinic);
        setShowDeactivateModal(true);
        break;
      default:
        break;
    }
  };

  const handleAddClinic = async (name) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/add-clinic`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowAddModal(false);
        toast.success("Clinic added successfully");
        await fetchClinics();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to add clinic. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEditClinic = async (name) => {
    if (!actionClinic) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-clinic/${actionClinic.id}`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowEditModal(false);
        setActionClinic(null);
        toast.success("Clinic updated successfully");
        await fetchClinics();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to update clinic. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateClinic = async () => {
    if (!actionClinic) return;
    setDeactivating(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/deactivate-clinic/${actionClinic.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowDeactivateModal(false);
        setActionClinic(null);
        toast.success("Clinic deactivated successfully");
        await fetchClinics();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to deactivate clinic. Please try again.",
      );
    } finally {
      setDeactivating(false);
    }
  };

  const handleRowClick = (clinic) => {
    navigate(`/clinic-detail/${clinic.id}`);
  };

  return (
    <>
      <style>{`
        .clinics-table-container {
          width: 100%;
          overflow-x: auto;
        }
        .clinics-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .clinics-table th,
        .clinics-table td {
          white-space: nowrap;
          border-bottom: 1px solid #EAECEF;
          vertical-align: middle;
          padding: 14px 16px;
        }
        .clinics-table thead th {
          background-color: #064260;
          color: #ffffff;
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .clinics-table tbody tr {
          cursor: pointer;
        }
        .clinics-table tbody tr:nth-child(even) td {
          background-color: #F8FAFC;
        }
        .clinics-table tbody tr:hover td {
          background-color: #EBF3FF !important;
        }
      `}</style>

      <div
        className="bg-white rounded shadow-sm"
        style={{ width: "100%", display: "flex", flexDirection: "column" }}
      >
        <div className="app-page-toolbar">
          <h2 className="app-page-title">Clinics</h2>

          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div className="app-filter-search-wrap">
              <FaSearch className="app-filter-search-icon" />
              <Form.Control
                type="text"
                className="app-filter-search"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "220px" }}
              />
            </div>

            <Dropdown
              onSelect={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
            >
              <Dropdown.Toggle
                className="custom-dropdown-toggle app-filter-dropdown"
                style={{ width: "140px" }}
              >
                {statusFilter}
                <FaChevronDown
                  size={11}
                  style={{ opacity: 0.7, flexShrink: 0, marginLeft: "6px" }}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="All">All</Dropdown.Item>
                <Dropdown.Item eventKey="Active">Active</Dropdown.Item>
                <Dropdown.Item eventKey="Inactive">Inactive</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button
              onClick={() => setShowAddModal(true)}
              style={{
                backgroundColor: "#27B99A",
                border: "none",
                borderRadius: "8px",
                height: "45px",
                padding: "0 24px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Add New
            </Button>
          </div>
        </div>

        <div className="clinics-table-container pb-2">
          <table className="clinics-table">
            <thead>
              <tr>
                <th style={{ minWidth: "80px" }}>ID</th>
                <th style={{ minWidth: "220px" }}>Clinic Name</th>
                <th style={{ minWidth: "130px" }}>Total Patients</th>
                <th style={{ minWidth: "130px" }}>Active Patients</th>
                <th style={{ minWidth: "110px" }}>App Users</th>
                <th style={{ minWidth: "100px" }}>Status</th>
                <th style={{ minWidth: "90px" }}>Actions</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px", color: "#444" }}>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-muted">
                    Loading clinics...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-muted">
                    No clinics found
                  </td>
                </tr>
              ) : (
                paginatedData.map((clinic) => (
                  <tr
                    key={clinic.id}
                    onClick={() => handleRowClick(clinic)}
                  >
                    <td style={{ fontFamily: "monospace", color: "#064260" }}>
                      {clinic.clinicId}
                    </td>
                    <td className="fw-semibold" style={{ color: "#064260" }}>
                      {clinic.name}
                    </td>
                    <td>{clinic.totalPatients}</td>
                    <td>{clinic.activePatients}</td>
                    <td>{clinic.appUsers}</td>
                    <td>
                      <StatusPill status={clinic.status} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "inline-block" }}>
                        <button
                          type="button"
                          className="actions-menu-btn"
                          aria-label="Clinic actions"
                          aria-expanded={openDropdownId === clinic.id}
                          onClick={(e) => toggleDropdown(clinic.id, e)}
                          style={{
                            backgroundColor: "transparent",
                            border: "1px solid #27B99A",
                            color: "#27B99A",
                            borderRadius: "6px",
                            fontSize: "14px",
                            padding: "6px 10px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaEllipsisV size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <TablePaginationBar
          currentPage={currentPage}
          totalItems={clinics.length}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(n) => setRowsPerPage(n)}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>

      <ClinicActionsDropdown
        openDropdownId={openDropdownId}
        dropdownPos={dropdownPos}
        clinics={paginatedData}
        onAction={handleClinicAction}
      />

      {showAddModal && (
        <AddEditClinicModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddClinic}
          isSubmitting={saving}
        />
      )}

      {showEditModal && actionClinic && (
        <AddEditClinicModal
          clinic={actionClinic}
          onClose={() => {
            setShowEditModal(false);
            setActionClinic(null);
          }}
          onSave={handleEditClinic}
          isSubmitting={saving}
        />
      )}

      {showDeactivateModal && actionClinic && (
        <DeactivateClinicModal
          clinic={actionClinic}
          onClose={() => {
            setShowDeactivateModal(false);
            setActionClinic(null);
          }}
          onConfirm={handleDeactivateClinic}
          isSubmitting={deactivating}
        />
      )}
    </>
  );
};

export default ClinicsTab;
